import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Add, ArrowDown2, ArrowUp2, Trash } from "iconsax-react";
import { ArrowRightIcon } from "@/shared/assets/icons";
import { EmptyState } from "@/shared/components";
import { useAppSelector } from "@/shared/hooks";
import { Button, ToggleButton, Toast } from "@/shared/ui";
import {
  useDeleteEducationMutation,
  useGetEducationQuery,
  useSetEducationFlagMutation,
} from "./api";
import EducationForm from "./EducationForm";
import EducationListSkeleton from "./EducationListSkeleton";
import type { EducationRecord } from "./types";

const DEGREE_VALUES = ["bachelor", "master", "phd", "college", "secondary_general"];

interface Props {
  token: string;
  onNext: () => void;
}

export default function EducationList({ token, onNext }: Props) {
  const { t } = useTranslation("ApplyApplicationPage");

  const { data, isLoading } = useGetEducationQuery(token);
  const [deleteEducation, deleteState] = useDeleteEducationMutation();
  const [setEducationFlag, flagState] = useSetEducationFlagMutation();

  const flagFromServer = useAppSelector((s) => s.completeness.data?.sections.education?.has_no_education);

  const list = data ?? [];
  const [openId, setOpenId] = useState<number | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [hasNoEducation, setHasNoEducation] = useState(flagFromServer ?? false);

  const [toast, setToast] = useState<{
    text: string;
    color: "positive" | "negative" | "notice" | "grey";
  } | null>(null);

  function toggleOpen(id: number) {
    setOpenId((old) => (old === id ? null : id));
  }

  async function handleFlagChange(checked: boolean) {
    setHasNoEducation(checked);
    setIsAdding(false);
    setOpenId(null);
    try {
      await setEducationFlag({ token, has_no_education: checked }).unwrap();
    } catch {
      setHasNoEducation(!checked);
      setToast({ color: "negative", text: "education.flag_error" });
    }
  }

  async function handleDelete(id: number) {
    try {
      await deleteEducation({ token, id }).unwrap();
      if (openId === id) setOpenId(null);
      setToast({ color: "positive", text: "education.toast.delete_success" });
    } catch {
      setToast({ color: "negative", text: "education.toast.delete_error" });
    }
  }

  function getDegreeLabel(value: string): string {
    if (!value) return "";
    return DEGREE_VALUES.includes(value) ? t(`education.degree.options.${value}`) : value;
  }

  function renderHeader(item: EducationRecord) {
    const title = [getDegreeLabel(item.degree), item.university_name].filter(Boolean).join(" · ");
    return title || t("education.new_form");
  }

  if (isLoading) {
    return <EducationListSkeleton />;
  }

  return (
    <div className="flex flex-col gap-8">
      <div className="flex justify-between items-center">
        <h2 className="text-display-2xs content-base-primary">{t("education.title")}</h2>

        {!hasNoEducation && (
          <Button
            variant="tertiary"
            size="lg"
            disabled={isAdding}
            onClick={() => {
              setIsAdding(true);
              setOpenId(null);
            }}
            className="self-start flex items-center gap-2">
            {t("education.add_education")}
            <Add size={20} color="currentColor" />
          </Button>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 pb-4 border-b surface-base-stroke">
        <span className="text-body-regular-md content-base-secondary">{t("education.no_education_label")}</span>
        <ToggleButton checked={hasNoEducation} onChange={handleFlagChange} disabled={flagState.isLoading} />
      </div>

      {hasNoEducation ? (
        <p className="text-body-regular-sm content-base-secondary p-3 surface-secondary-fill radius-sm">
          {t("education.no_education_note")}
        </p>
      ) : (
        <div className="flex flex-col gap-6">
          {list.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div key={item.id} className="flex flex-col gap-4">
                <div className="flex justify-between items-center border-b pb-4 surface-base-stroke">
                  <button
                    type="button"
                    className="flex justify-between items-center flex-1 cursor-pointer gap-3"
                    onClick={() => toggleOpen(item.id)}>
                    <p className="text-body-bold-lg content-base-primary text-left">{renderHeader(item)}</p>

                    <div className="flex items-center gap-2">
                      <span className="input-box-shadow radius-2xs w-6 h-6 flex items-center justify-center content-base-secondary">
                        {isOpen ? (
                          <ArrowUp2 size={16} color="currentColor" />
                        ) : (
                          <ArrowDown2 size={16} color="currentColor" />
                        )}
                      </span>
                      <Button
                        variant="destructive"
                        isIconButton
                        size="sm"
                        onClick={() => handleDelete(item.id)}
                        disabled={deleteState.isLoading}
                        className="w-6! aspect-square! rounded-[4px]! p-0!">
                        <Trash size={16} color="currentColor" />
                      </Button>
                    </div>
                  </button>
                </div>

                {isOpen && (
                  <EducationForm
                    token={token}
                    record={item}
                    onCancel={() => setOpenId(null)}
                    onSuccess={() => setOpenId(null)}
                    onToast={(next) => setToast(next)}
                  />
                )}
              </div>
            );
          })}

          {list.length === 0 && !isAdding && (
            <EmptyState compact noDataText={t("education.empty")} noDataHint={t("education.emptyHint")} />
          )}

          {isAdding && (
            <div className="flex flex-col gap-6 pt-2">
              <EducationForm
                token={token}
                onCancel={() => setIsAdding(false)}
                onSuccess={() => setIsAdding(false)}
                onToast={(next) => setToast(next)}
              />
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button variant="primary" size="lg" type="button" onClick={onNext}>
          {t("education.next_section")}
          <span className="text-white">
            <ArrowRightIcon />
          </span>
        </Button>
      </div>

      {toast && (
        <Toast
          key={`${toast.color}-${toast.text}`}
          color={toast.color}
          text={t(toast.text)}
          isFullWidth
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
